import { reaction } from "mobx";
import type { ApiClient } from "@/clients/ApiClient";
import { clearLocalData, isEmpty, readLocalData, type ImportResult } from "@/core/localImport";
import { clearDeviceFollows, readDeviceFollows } from "@/core/deviceFollows";

type ImportApi = Pick<ApiClient, "post">;

interface ImportAuth {
    readonly status: "unknown" | "anonymous" | "authenticated";
}

/**
 * Moves what this browser kept before accounts (follows, the playlist, listening progress) onto the
 * account of whoever signs in. It runs whenever a session starts and there is something left on the
 * device, so a failed attempt is tried again on the next visit. The device copy is only deleted once
 * the account has it; the API merges, so trying twice does no harm.
 */
export class LocalImportStore {
    private running = false;
    private current: Promise<unknown> = Promise.resolve();

    constructor(
        private readonly client: ImportApi,
        private readonly auth: ImportAuth,
        /** Called after a successful import, so the stores can read the account again */
        private readonly onImported: () => Promise<void> | void,
        /** Called when something was moved, for the one-time notice */
        private readonly onMoved: (result: ImportResult) => void,
        /**
         * Called when this device has podcasts followed without an account. Nothing is sent until the listener
         * answers: choose(true) adds them to the account, choose(false) drops them. Resolves to whether it worked.
         */
        private readonly onDeviceFollows: (count: number, choose: (add: boolean) => Promise<boolean>) => void
    ) {
        reaction(
            () => this.auth.status,
            (status) => {
                if (status === "authenticated") void this.run();
            },
            { fireImmediately: true }
        );
    }

    /** Resolves when no import is under way, e.g. before acting on the account's lists */
    async whenIdle(): Promise<void> {
        await this.current;
    }

    run(): Promise<boolean> {
        const attempt = this.attempt();
        this.current = attempt;
        return attempt;
    }

    /** Adds the podcasts followed without an account to the account, or drops them. The device copy goes only once done. */
    private async decideDeviceFollows(ids: number[], add: boolean): Promise<boolean> {
        try {
            if (add) {
                await this.client.post<ImportResult>("/me/import", { follows: ids, playlist: [], listening: [] });
                await this.onImported();
            }
            await clearDeviceFollows();
            return true;
        } catch (error) {
            console.warn("Could not move the podcasts followed on this device", error);
            return false;
        }
    }

    private async attempt(): Promise<boolean> {
        if (this.running) return false;
        this.running = true;
        try {
            const body = await readLocalData();
            if (!isEmpty(body)) {
                const result = await this.client.post<ImportResult>("/me/import", body);
                await clearLocalData();
                await this.onImported();
                const moved = result.imported.follows + result.imported.playlist + result.imported.listening;
                if (moved > 0) this.onMoved(result);
            }

            const deviceFollows = await readDeviceFollows();
            if (deviceFollows.length > 0) {
                const ids = deviceFollows.map((podcast) => podcast.id);
                this.onDeviceFollows(ids.length, (add) => this.decideDeviceFollows(ids, add));
            }
            return true;
        } catch (error) {
            // Nothing is deleted: the data is still on the device for the next attempt
            console.warn("Could not move this device's data to the account", error);
            return false;
        } finally {
            this.running = false;
        }
    }
}
